import { accessBlockers } from './contactAccessModel';
import { rules, validateField, type FormSchema } from '../ui/fieldModel';

const isPasswordKey = (k: string) => k === 'contacts.accessNeedsPassword' || k === 'contacts.passwordWeak';

/**
 * W-07: "עובד לקוח (בלי איש קשר)" - the plain customer employee, created with
 * a login and a grant only. Pure helpers for the form: which field is missing
 * or invalid, in screen order (the first one gets the focus - W-08b).
 */
export type StaffField = 'org' | 'name' | 'email' | 'password';

export interface StaffDraft {
  orgId: string;
  name: string;
  email: string;
  password: string;
}

export interface StaffProblem {
  field: StaffField;
  /** i18n key of the problem line. */
  key: string;
}

/** DOM id of each field (the focus target). */
export const STAFF_FIELD_ID: Record<StaffField, string> = {
  org: 'pa-org',
  name: 'pa-staff-name',
  email: 'pa-staff-email',
  password: 'pa-staff-password',
};

export function staffProblems(d: StaffDraft): StaffProblem[] {
  const out: StaffProblem[] = [];
  if (!d.orgId) out.push({ field: 'org', key: 'portalAccess.staff.needOrg' });
  if (!d.name.trim()) out.push({ field: 'name', key: 'portalAccess.staff.needName' });
  // level is fixed ("עובד לקוח"), so the shared blocker is asked for e-mail + password only.
  // W-08c: every reason is listed (e-mail AND password), not only the first.
  for (const key of accessBlockers({ email: d.email, level: 'customer_employee', password: d.password })) {
    out.push({ field: isPasswordKey(key) ? 'password' : 'email', key });
  }
  return out;
}

export function buildStaffBody(d: StaffDraft, active: boolean) {
  return {
    mode: 'customer_staff' as const,
    organization_id: d.orgId,
    name: d.name.trim(),
    email: d.email.trim(),
    password: d.password,
    active,
  };
}

/**
 * W-08b / W-07: scroll to the FIRST missing or invalid field and focus it
 * (the summary block stays at the top). `root` is the form/dialog element.
 */
/** Where the dialog body must scroll so `el` sits in the middle of it (never below 0). */
export function scrollTopToCenter(m: { containerTop: number; containerHeight: number; elTop: number; elHeight: number; scrollTop: number }): number {
  const delta = (m.elTop - m.containerTop) - (m.containerHeight / 2 - m.elHeight / 2);
  return Math.max(0, Math.round(m.scrollTop + delta));
}

/** The closest ancestor that scrolls vertically (the dialog body) - never the window. */
function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let n = el.parentElement; n; n = n.parentElement) {
    const oy = getComputedStyle(n).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight) return n;
  }
  return null;
}

export function focusFirstProblem(ids: string[], doc: Document = document): boolean {
  for (const id of ids) {
    const el = doc.getElementById(id) as HTMLElement | null;
    // A disabled control cannot take the focus (e.g. "לכל העובדים כבר יש גישה") - the next problem does.
    if (!el || (el as HTMLInputElement).disabled) continue;
    // W-08c: scroll the dialog BODY (deterministic, not smooth - a smooth scroll can be cut by the focus).
    const box = scrollParent(el);
    if (box) {
      const c = box.getBoundingClientRect(); const r = el.getBoundingClientRect();
      box.scrollTop = scrollTopToCenter({ containerTop: c.top, containerHeight: c.height, elTop: r.top, elHeight: r.height, scrollTop: box.scrollTop });
    } else {
      el.scrollIntoView?.({ block: 'center' });
    }
    // A picker/button-like control takes focus too; an input is focused directly.
    el.focus?.({ preventScroll: true });
    return true;
  }
  return false;
}

/* ───────── W-08c: ONE source of truth for "why can't I save" ───────── */

/**
 * One reason a save is refused. The form lists every one of them in the
 * summary, marks each control red and focuses the first. There is no other
 * way a save click can stop (no silent `return`, no disabled save button).
 */
export interface SaveProblem {
  /** DOM id of the control: marked red, and focused when it is the first. */
  id: string;
  /** i18n key of the field's label (the summary line). */
  labelKey: string;
  /** i18n key of the message under the field. */
  key: string;
  params?: Record<string, unknown>;
  /** 'missing' = the summary shows the label; 'invalid' = label + message. */
  kind: 'missing' | 'invalid';
}

/** The summary line of a problem (`t` = i18next's). */
export function saveProblemLine(p: SaveProblem, t: (k: string, o?: Record<string, unknown>) => string): string {
  return p.kind === 'missing' ? t(p.labelKey) : `${t(p.labelKey)}: ${t(p.key, p.params)}`;
}

/** The first problem on a control, or undefined. */
export function problemOn(problems: SaveProblem[], id: string): SaveProblem | undefined {
  return problems.find((p) => p.id === id);
}

/** The contact's own fields (validated on blur too - `useFormValidation`). Keys = control ids. */
export const CONTACT_SCHEMA: FormSchema = {
  'contact-name': [rules.required(), rules.minLength(2, 'validation.nameTooShort')],
  'contact-type': [rules.required()],
  'contact-email': [rules.email()],
  'contact-phone': [rules.maxLength(50, 'validation.phoneTooLong')],
  'contact-mobile': [rules.maxLength(50, 'validation.phoneTooLong')],
};
const CONTACT_LABEL_KEY: Record<string, string> = {
  'contact-name': 'contacts.name',
  'contact-type': 'contacts.contactType',
  'contact-email': 'contacts.email',
  'contact-phone': 'contacts.phone',
  'contact-mobile': 'contacts.mobile',
};
/** The contact-type control is a radio group: the id sits on its first radio. */
export const CONTACT_TYPE_FIRST_ID = 'contact-type';
export const CONTACT_ORG_ID = 'contact-organization-id';

/** Ids inside `PortalAccessFields` for an `idPrefix`. */
export function accessFieldId(prefix: string, part: 'email' | 'level' | 'password'): string {
  return `${prefix}-access-${part}`;
}

/** Every reason the access block (`PortalAccessFields`) refuses, as save problems. */
export function accessSaveProblems(prefix: string, a: { email: string; level?: string | null; password: string }): SaveProblem[] {
  return accessBlockers({ email: a.email, level: (a.level ?? '') as never, password: a.password }).map((key) => {
    const part = isPasswordKey(key) ? 'password' : key === 'contacts.accessNeedsType' ? 'level' : 'email';
    const labelKey = part === 'password' ? 'contacts.password' : part === 'level' ? 'contacts.accessType' : 'contacts.accessEmail';
    return { id: accessFieldId(prefix, part), labelKey, key, kind: key.startsWith('contacts.accessNeeds') ? 'missing' : 'invalid' };
  });
}

/**
 * The contact form (ContactFormModal - Contacts page, customer page, portal
 * access "+ איש קשר חדש"): customer, the contact's fields (the same schema the
 * blur check uses), then the portal-access block. Screen order.
 */
export function contactFormProblems(a: {
  /** A new contact without a fixed customer: the customer picker is shown and required. */
  orgRequired: boolean;
  orgId: string;
  values: { name: string; type: string; email: string; phone: string; mobile: string };
  /** The access block when it is ON (create + "יצירת גישת פורטל"), else null. */
  access: { email: string; level?: string | null; password: string } | null;
  accessPrefix?: string;
}): SaveProblem[] {
  const out: SaveProblem[] = [];
  if (a.orgRequired && !a.orgId) out.push({ id: CONTACT_ORG_ID, labelKey: 'contacts.organization', key: 'validation.required', kind: 'missing' });
  const values: Record<string, unknown> = {
    'contact-name': a.values.name, 'contact-type': a.values.type, 'contact-email': a.values.email,
    'contact-phone': a.values.phone, 'contact-mobile': a.values.mobile,
  };
  for (const id of Object.keys(CONTACT_SCHEMA)) {
    const issue = validateField(CONTACT_SCHEMA, id, values);
    if (!issue) continue;
    const missing = issue.key === 'validation.required';
    out.push({
      id: id === 'contact-type' ? CONTACT_TYPE_FIRST_ID : id,
      labelKey: CONTACT_LABEL_KEY[id],
      key: id === 'contact-type' && missing ? 'contacts.typeRequired' : issue.key,
      params: issue.params as Record<string, unknown> | undefined,
      kind: missing ? 'missing' : 'invalid',
    });
  }
  if (a.access) out.push(...accessSaveProblems(a.accessPrefix ?? 'contact-new', a.access));
  return out;
}

const STAFF_LABEL_KEY: Record<StaffField, string> = {
  org: 'portalAccess.organization', name: 'contacts.name', email: 'contacts.accessEmail', password: 'contacts.password',
};

/**
 * The "גישה חדשה" form of the Portal-access page (create): the OTE-employee
 * track, the existing-contact track and the customer-employee track (W-07).
 * The "+ איש קשר חדש" track is the contact form (`contactFormProblems`).
 */
export function grantFormProblems(a: {
  track: 'employee' | 'customer';
  source: 'contact' | 'new' | 'staff';
  employeeId: string;
  /** The picked employee has an e-mail (the server needs one). */
  employeeHasEmail: boolean;
  /** A password was typed on the employee track. */
  passwordTyped: boolean;
  orgId: string;
  contactId: string;
  /** The access block of the existing-contact track, once a contact is picked; else null. */
  contactAccess: { email: string; level?: string | null; password: string } | null;
  staff: { name: string; email: string; password: string };
}): SaveProblem[] {
  const out: SaveProblem[] = [];
  if (a.track === 'employee') {
    if (!a.employeeId) out.push({ id: 'pa-employee', labelKey: 'portalAccess.employee', key: 'validation.required', kind: 'missing' });
    else if (!a.employeeHasEmail) out.push({ id: 'pa-employee', labelKey: 'portalAccess.employee', key: 'portalAccess.employeeNoEmail', kind: 'invalid' });
    if (!a.passwordTyped) out.push({ id: 'pa-password', labelKey: 'portalAccess.password', key: 'validation.required', kind: 'missing' });
    return out;
  }
  if (a.source === 'staff') {
    return staffProblems({ orgId: a.orgId, ...a.staff }).map((p) => ({
      id: STAFF_FIELD_ID[p.field], labelKey: STAFF_LABEL_KEY[p.field], key: p.key,
      kind: p.field === 'org' || p.field === 'name' || p.key.startsWith('contacts.accessNeeds') ? 'missing' : 'invalid',
    }));
  }
  if (a.source === 'contact') {
    if (!a.orgId) out.push({ id: 'pa-org', labelKey: 'portalAccess.organization', key: 'validation.required', kind: 'missing' });
    if (!a.contactId) out.push({ id: 'pa-contact', labelKey: 'portalAccess.contact', key: 'validation.required', kind: 'missing' });
    if (a.contactAccess) out.push(...accessSaveProblems('pa-contact', a.contactAccess));
  }
  return out;
}
